import { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

import { MenuItem, TextField } from "@mui/material";

import { faClipboardList } from "@fortawesome/pro-regular-svg-icons";

import { Icon } from "@/components";
import { Modal } from "@/components/Modals/Modal";

import {
	useCreateTaskTemplateMutation,
	useUpdateTaskTemplateMutation,
} from "../../../redux/task-template/task-template.api";
import {
	CreateTaskTemplate,
	CreateTaskTemplateSchema,
	TaskTemplateInfo,
} from "../schema/task-template";
import { ToDoPriority } from "../../TasksAndSubTasks/enum/ToDoPriority.enum";
import { SubTaskTemplateList } from "./SubTaskTemplateList";

const fieldSx = {
	"& .MuiInputBase-root": {
		borderRadius: "12px",
		fontSize: "14px",
	},
	"& .MuiOutlinedInput-notchedOutline": {
		borderColor: "#E6E6EC",
	},
};

interface TaskTemplateModalProps {
	isOpen: boolean;
	template: TaskTemplateInfo | null;
	onClose: () => void;
}

export const TaskTemplateModal = ({ isOpen, template, onClose }: TaskTemplateModalProps) => {
	const { t } = useTranslation();
	const ts = (key: string) => t(`manage-task-templates.${key}`);

	const [createTemplate, { isLoading: isCreating }] = useCreateTaskTemplateMutation();
	const [updateTemplate, { isLoading: isUpdating }] = useUpdateTaskTemplateMutation();

	const isEditing = !!template?.id;

	const {
		control,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<CreateTaskTemplate>({
		resolver: zodResolver(CreateTaskTemplateSchema),
		defaultValues: {
			title: "",
			description: "",
			priority: undefined,
			complexityEstimate: undefined,
		},
	});

	useEffect(() => {
		if (template) {
			reset({
				title: template.title,
				description: template.description || "",
				priority: template.priority || undefined,
				complexityEstimate: template.complexityEstimate || undefined,
			});
		} else {
			reset({
				title: "",
				description: "",
				priority: undefined,
				complexityEstimate: undefined,
			});
		}
	}, [template, reset]);

	const onSubmit = async (data: CreateTaskTemplate) => {
		if (isEditing && template?.id) {
			await updateTemplate({ id: template.id, ...data });
		} else {
			await createTemplate(data);
		}
		onClose();
	};

	return (
		<Modal
			aboveHeader={<Icon className="w-4 h-4" icon={faClipboardList} />}
			dialogContentClassName="!p-[2px]"
			handleClose={onClose}
			handleSave={handleSubmit(onSubmit)}
			isLoading={isCreating || isUpdating}
			isOpened={isOpen}
			size="md"
			submitButtonText={isEditing ? ts("update") : ts("create")}
			subtitle={isEditing ? ts("edit-subtitle") : ts("create-subtitle")}
			title={isEditing ? ts("edit-title") : ts("create-title")}
		>
			<form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
				<div className="w-full flex flex-col gap-2">
					<p className="text-[14px] font-bold leading-5 text-primary-blue-dark">
						{ts("form.title")}
					</p>
					<Controller
						control={control}
						name="title"
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								error={!!errors.title}
								helperText={errors.title?.message}
								inputProps={{ style: { padding: "10px 14px", fontSize: "14px" } }}
								size="small"
								sx={fieldSx}
							/>
						)}
					/>
				</div>

				<div className="w-full flex flex-col gap-2">
					<p className="text-[14px] font-bold leading-5 text-primary-blue-dark">
						{ts("form.description")}
					</p>
					<Controller
						control={control}
						name="description"
						render={({ field }) => (
							<TextField
								{...field}
								fullWidth
								multiline
								maxRows={4}
								minRows={3}
								size="small"
								sx={fieldSx}
								value={field.value ?? ""}
							/>
						)}
					/>
				</div>

				<div className="grid grid-cols-2 gap-4 items-start">
					<div className="w-full flex flex-col gap-2">
						<p className="text-[14px] font-bold leading-5 text-primary-blue-dark">
							{ts("form.priority")}
						</p>
						<Controller
							control={control}
							name="priority"
							render={({ field }) => (
								<TextField
									fullWidth
									select
									inputProps={{ style: { fontSize: "14px" } }}
									size="small"
									sx={fieldSx}
									value={field.value ?? ""}
									onChange={(e) =>
										field.onChange(e.target.value === "" ? undefined : e.target.value)
									}
								>
									<MenuItem value="">—</MenuItem>
									{Object.values(ToDoPriority).map((p) => (
										<MenuItem key={p} value={p}>
											{p}
										</MenuItem>
									))}
								</TextField>
							)}
						/>
					</div>

					<div className="w-full flex flex-col gap-2">
						<p className="text-[14px] font-bold leading-5 text-primary-blue-dark">
							{ts("form.complexity")}
						</p>
						<Controller
							control={control}
							name="complexityEstimate"
							render={({ field }) => (
								<TextField
									{...field}
									fullWidth
									inputProps={{ style: { padding: "10px 14px", fontSize: "14px" } }}
									size="small"
									sx={fieldSx}
									type="number"
									value={field.value ?? ""}
									onChange={(e) =>
										field.onChange(e.target.value ? Number(e.target.value) : undefined)
									}
								/>
							)}
						/>
					</div>
				</div>

				{isEditing && template?.id && <SubTaskTemplateList taskTemplateId={template.id} />}
			</form>
		</Modal>
	);
};
